// thunks.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createAsyncThunk } from '@reduxjs/toolkit'
import { buildAuthenticatedUser, AuthenticatedUser } from '../../common/authenticatedUser'
import CMCClient from '../../data/cmcClient'
import { Company, createNewSiteTag, renameCompanySiteTag } from '../../data/companyData'
import { CompanyList } from './slice'

export const getCompanyList = createAsyncThunk(
  'company/getCompanyList',
  async (credentials: {
    userId: string;
    sessionId: string;
    dataCenter: string;
  }): Promise<CompanyList> => {
    const { userId, sessionId, dataCenter } = credentials
    const authUser = buildAuthenticatedUser(sessionId, dataCenter, userId)
    const response = await CMCClient.getCompanies(authUser)
    return response.reduce<CompanyList>((acc, data) => {
      acc[data.id] = {
        ...data,
        label: `${data.name} - (${data.id})`
      }
      return acc
    }, {})
  }
)

export const setSelectedCompany = createAsyncThunk(
  'company/setSelectedCompany',
  async (args: {
    userId: string
    sessionId: string
    dataCenter: string
    company: Company
  }): Promise<Company> => {
    const {
      company
    } = args
    return {
      ...company,
      allTags: await CMCClient.getSelectedCompanyTags()
    }
  }
)

/**
 * Creates a new tag and updates the backend data for the provided company
 */
export const createNewTag = createAsyncThunk(
  'company/createNewTag',
  async (args: {
    authUser: AuthenticatedUser,
    companyId: string,
    tagLabel: string
  }) => ({
    companyId: args.companyId,
    tag: await createNewSiteTag(
      args.authUser,
      args.tagLabel
    )
  })
)

export const renameTag = createAsyncThunk(
  'company/renameTag',
  async (args: {
    authUser: AuthenticatedUser,
    companyId: string,
    tagId: string,
    newTagLabel: string
  }) => {
    const response = await renameCompanySiteTag(
      args.authUser,
      args.newTagLabel,
      args.tagId
    )
    if (response === false) {
      // TODO Response indicates that it didn't work
    }
    return {
      companyId: args.companyId,
      tagId: args.tagId,
      newTagLabel: args.newTagLabel
    }
  }
)
